/**
 * オフライン地図（PMTiles）のダウンロード・状態確認・削除。
 *
 * Service Worker（`sw.ts`）側は「Cache Storage に入っていれば Range で
 * 切り出して返す」だけの読み出し専用ルートで、書き込みはここだけが行う
 * （理由は sw.ts のコメント参照）。
 *
 * 状態はローカルに別途フラグを持たず、常に Cache Storage を正として読む。
 * OS に黙って消された場合も、次にこの関数を呼んだ瞬間に「未取得」へ
 * 正しく戻る（docs/04「消えても即座に検知して直す」をそのまま満たす）。
 */

/** sw.ts と同じ名前を使うこと（キャッシュを共有するため） */
export const PMTILES_CACHE = 'gv4d-pmtiles';

export interface OfflineMapStatus {
  downloaded: boolean;
  /** 保存済みファイルの大きさ [バイト]。取得できなければ null */
  bytes: number | null;
}

export async function offlineMapStatus(url: string): Promise<OfflineMapStatus> {
  if (!('caches' in self)) return { downloaded: false, bytes: null };
  const cache = await caches.open(PMTILES_CACHE);
  const cached = await cache.match(url);
  if (!cached) return { downloaded: false, bytes: null };
  const len = cached.headers.get('content-length');
  return { downloaded: true, bytes: len ? Number(len) : null };
}

/**
 * 先頭バイトが PMTiles のマジックナンバー（"PM"）で始まっているか。
 *
 * `pmtiles` パッケージ自身がヘッダ読み込み時に同じ値で検証している
 * （`node_modules/pmtiles` の `bytesToHeader` 相当）。
 *
 * ★ 実際に踏んだ不具合: URL を間違えた、あるいは会場ファイルがまだ
 *   置かれていない場合、静的サーバーの SPA フォールバックが index.html を
 *   200 OK で返すことがある（Vite の既定動作）。`res.ok` だけを見ると
 *   「成功」扱いになり、ページの HTML を地図データとして保存してしまう
 *   （実際に 15KB＝index.html相当のサイズで「取得済み」と表示される
 *   事故として発生）。保存前にここで弾く。
 */
export function isPmtilesMagic(bytes: Uint8Array): boolean {
  return bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4d; // 'P', 'M'
}

/**
 * ファイル全体を1回で取得して Cache Storage に保存する。
 *
 * ★ Range リクエストではなく、必ず素の GET で丸ごと取ること。
 *   ここで保存した Response が、sw.ts の Range 切り出しルートの
 *   「完全なファイル」の元になる。
 */
export async function downloadOfflineMap(
  url: string,
  onProgress: (loaded: number, total: number | null) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(url, { signal });
  if (!res.ok || !res.body) {
    throw new Error(`ダウンロードに失敗しました（HTTP ${res.status}）`);
  }

  const total = Number(res.headers.get('content-length')) || null;
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress(loaded, total);
  }

  if (!chunks[0] || !isPmtilesMagic(chunks[0])) {
    throw new Error(
      '地図ファイルが見つかりません（別のファイルが返ってきました）。' +
        '会場データが正しく配置されているか確認してください。',
    );
  }

  // BlobPart[] は ArrayBufferView を受け付けるが、TS の型定義上は
  // Uint8Array<ArrayBufferLike> とは厳密には一致しないため明示キャストする
  const blob = new Blob(chunks as BlobPart[]);
  const cache = await caches.open(PMTILES_CACHE);
  try {
    await cache.put(
      url,
      new Response(blob, {
        status: 200,
        headers: {
          'Content-Type': 'application/octet-stream',
          'Content-Length': String(blob.size),
        },
      }),
    );
  } catch (e) {
    // 保存領域が足りない場合など。ダウンロード自体は成功しているので
    // その旨が伝わるようにする
    throw new Error(`端末への保存に失敗しました: ${(e as Error).message}`);
  }
}

export async function deleteOfflineMap(url: string): Promise<void> {
  const cache = await caches.open(PMTILES_CACHE);
  await cache.delete(url);
}
